import type { ShortcutCommand, ShortcutAction } from '@/types/keyboard';
import { useUIStore } from '@/stores/uiStore';
import { useTodoViewStore } from '@/stores/todoViewStore';
import { useBulkOperationsStore } from '@/stores/bulkOperationsStore';
import { useHoverStore } from '@/stores/hoverStore';
import { useEmailStore } from '@/stores/emailStore';
import { useCommandPaletteStore } from '@/stores/commandPaletteStore';
import { useUndoStore } from '@/stores/undoStore';
import { useTriageStore } from '@/stores/triageStore';
import { useKeyboardStore } from '@/stores/keyboardStore';
import { useSenderGroupingStore } from '@/stores/senderGroupingStore';
import { useMoveToFolderStore } from '@/stores/moveToFolderStore';
import { emitTodoCommand } from '@/features/todo/utils/todoCommands';
import { getPointerTarget } from './pointerTarget';
import { isTypingContext } from './keyboardUtils';
import { executeEmailAction } from '@/stores/emailCommands';
import { useSearchStore } from '@/stores/searchStore';
import { useAccountStore } from '@/stores/accountStore';
import { navigateToMailView } from '@/features/mailbox/mailViewNavigation';
import { mailEvents } from '@/events/mailEvents';
import { toast } from 'sonner';
import type { SenderGroupInfo, SenderGroupQuickAction } from '@/features/email-list/types';

// Registry of all available commands
class KeyboardRegistry {
  private commands = new Map<ShortcutAction, ShortcutCommand>();

  register(command: ShortcutCommand) {
    this.commands.set(command.action, command);
  }

  get(action: ShortcutAction): ShortcutCommand | undefined {
    return this.commands.get(action);
  }

  getAll(): ShortcutCommand[] {
    return Array.from(this.commands.values());
  }

  getByCategory(category: string): ShortcutCommand[] {
    return this.getAll().filter((cmd) => cmd.category === category);
  }
}

export const keyboardRegistry = new KeyboardRegistry();

/**
 * Format a keybinding for display
 * Returns an array of key parts that can be rendered as separate Kbd components
 */
export function formatKeyBinding(binding: ShortcutCommand['defaultBinding']): string[] {
  const parts: string[] = [];

  if (binding.ctrl) parts.push('Ctrl');
  if (binding.alt) parts.push('Alt');
  if (binding.shift) parts.push('Shift');
  if (binding.meta) parts.push('Meta');

  // Format the key
  let key = binding.key;
  if (key === 'ArrowLeft') key = '←';
  else if (key === 'ArrowRight') key = '→';
  else if (key === 'ArrowUp') key = '↑';
  else if (key === 'ArrowDown') key = '↓';
  else if (key === 'Delete') key = 'Del';
  else if (key === 'Escape') key = 'Esc';
  else if (key.length === 1) key = key.toUpperCase();

  parts.push(key);
  return parts;
}

/**
 * Get the keybinding for a specific action
 * Checks custom bindings from keyboard store first, then falls back to default
 */
export function getKeyBinding(action: ShortcutAction): ShortcutCommand['defaultBinding'] | null {
  const command = keyboardRegistry.get(action);
  if (!command) return null;

  // Check for custom binding in keyboard store
  const { customBindings, disabledShortcuts } = useKeyboardStore.getState();

  // If shortcut is disabled, return null
  if (disabledShortcuts.has(action)) return null;

  // Return custom binding if set, otherwise default
  const customBinding = customBindings[action];
  if (customBinding !== undefined) {
    return customBinding; // Can be null if user explicitly cleared it
  }

  return command.defaultBinding;
}

/** Normalized set of the user's own account addresses. */
function getOwnAccountEmails(): Set<string> {
  const set = new Set<string>();
  for (const account of useAccountStore.getState().accounts) {
    const address = account.emailAddress?.trim().toLowerCase();
    if (address) set.add(address);
  }
  return set;
}

/**
 * Build a SenderGroupInfo for the sender group that a given email belongs to.
 * Scans the provided email list (excluding `excludeIds`) to collect all emails
 * from the same sender and assembles a hover-ready group object.
 *
 * REFUSES to build a group keyed to one of the user's own addresses (N1): in
 * recipient-keyed views (Sent), every message is "from" the user, so a
 * from.email rebuild would span the whole loaded list and dispatch a
 * server-side sender bulk action against the user's own mail. Callers degrade
 * to single-row semantics on null.
 */
function buildSenderGroupFromEmail(
  targetEmail: ReturnType<typeof useEmailStore.getState>['emails'][0],
  allEmails: ReturnType<typeof useEmailStore.getState>['emails'],
  excludeIds: Set<string>,
): SenderGroupInfo | null {
  const senderEmail = targetEmail.from?.email?.toLowerCase()?.trim();
  if (!senderEmail) return null;
  if (getOwnAccountEmails().has(senderEmail)) return null;

  const groupEmails = allEmails.filter(
    (e) => !excludeIds.has(e.id) && e.from?.email?.toLowerCase()?.trim() === senderEmail,
  );
  if (!groupEmails.length) return null;

  return {
    key: senderEmail,
    contactId: groupEmails[0].contactId ?? null,
    contactApprovalState: groupEmails[0].contactApprovalState ?? null,
    senderEmail: groupEmails[0].from.email || senderEmail,
    senderName: groupEmails[0].from.name || groupEmails[0].from.email || senderEmail,
    emails: groupEmails,
    unreadCount: groupEmails.filter((e) => !e.read).length,
    totalMessages: groupEmails.length,
  };
}

export type ActionTargetResolution =
  | { kind: 'triage'; ids: string[] }
  /**
   * `complete` is false while the open conversation is still a provisional
   * shell (declared messageCount > hydrated messages): `ids` then holds only
   * the seed message, and conversation-level actions must refuse rather than
   * silently act on one message of N (same gate as the thread toolbar).
   */
  | { kind: 'thread'; ids: string[]; complete: boolean }
  | { kind: 'senderGroup'; ids: string[]; group: SenderGroupInfo | null }
  | { kind: 'emails'; ids: string[] }
  | { kind: 'none'; ids: string[] };

/**
 * THE action-target resolver for keyboard shortcuts. One precedence order,
 * consumed by every email shortcut handler AND its enabled():
 *
 *   triage mode → open sender digest (whole sender) → explicit bulk
 *   selection → open thread (whole conversation) / open detail (that email) →
 *   pointer hit-test (live elementFromPoint ground truth) →
 *   entered sender group (whole sender) →
 *   hover-store fallback (only reachable when the list is the primary view —
 *   any open detail/thread already returned above, so a stale hover can never
 *   target a hidden list row).
 *
 * In the search view only search-result rows (which carry data-email-row-id)
 * or the opened result are targetable — never the hidden mailbox list.
 */
export function resolveActionTarget(): ActionTargetResolution {
  // Triage mode owns shortcuts wholesale
  const triageStore = useTriageStore.getState();
  if (triageStore.isTriageMode) {
    return triageStore.currentContext
      ? { kind: 'triage', ids: [] }
      : { kind: 'none', ids: [] };
  }

  // Digest pane open: the digest IS the sender — shortcuts act on the whole
  // sender group it shows (never fall through to the group rows hidden behind
  // it, N20).
  const senderState = useSenderGroupingStore.getState();
  if (senderState.senderDigestVisible) {
    const digest = senderState.senderDigest;
    return digest
      ? { kind: 'senderGroup', ids: digest.emails.map((e) => e.id), group: digest.group }
      : { kind: 'none', ids: [] };
  }

  // Search view: hit-test the search rows; never the hidden mailbox list
  if (useUIStore.getState().view === 'search') {
    const pointer = getPointerTarget();
    if (pointer?.type === 'email' && pointer.ids.length) {
      return { kind: 'emails', ids: pointer.ids };
    }
    const selectedId = useEmailStore.getState().selectedEmailId;
    if (selectedId) {
      return { kind: 'emails', ids: [selectedId] };
    }
    return { kind: 'none', ids: [] };
  }

  // 1. Explicit bulk selection (multi-select intent)
  const bulkStore = useBulkOperationsStore.getState();
  if (bulkStore.bulkSelection.size > 0) {
    return { kind: 'emails', ids: Array.from(bulkStore.bulkSelection) };
  }

  // 2. Open thread / open detail — the thing the user is READING wins over
  // whatever the cursor happens to rest on.
  // Thread-ness uses the DECLARED size, exactly like the view's
  // showThreadView: a provisional shell (siblings still hydrating, or their
  // fetch failed) holds one message but IS a thread — keying on
  // messages.length made `e` archive only the seed message.
  const emailState = useEmailStore.getState();
  const thread = emailState.selectedThread;
  const declaredCount = thread ? thread.messageCount ?? thread.messages.length : 0;
  if (thread && declaredCount > 1) {
    const active = thread.messages.filter((m) => !m.isArchived && !m.isTrashed);
    const pool = active.length ? active : thread.messages;
    return {
      kind: 'thread',
      ids: pool.map((m) => m.id),
      complete: thread.messages.length >= declaredCount,
    };
  }
  if (emailState.selectedEmailId) {
    return { kind: 'emails', ids: [emailState.selectedEmailId] };
  }

  // 3. Pointer hit-test — the row physically under the cursor RIGHT NOW
  const pointer = getPointerTarget();
  if (pointer?.ids.length) {
    if (pointer.type === 'group') {
      const group = resolvePointerSenderGroup(pointer.groupKey, pointer.ids);
      if (!group) {
        // Unresolvable group identity: act on the row's rendered ids as plain
        // emails — never amplify into a server-side sender action (N1).
        return { kind: 'emails', ids: pointer.ids };
      }
      return { kind: 'senderGroup', ids: pointer.ids, group };
    }
    return { kind: 'emails', ids: pointer.ids };
  }

  // 4. Inside a sender group (its message list replaces the mailbox) with no
  // row under the pointer: the group itself is the target — the keyboard
  // equivalent of the group header's Archive/Delete-all.
  if (senderState.activeSenderGroup && senderState.activeSenderGroupInfo) {
    const group = senderState.activeSenderGroupInfo;
    return { kind: 'senderGroup', ids: group.emails.map((e) => e.id), group };
  }

  // 5. Hover-store fallback (pointer position unknown, e.g. before the first
  // mousemove). Only reachable with no detail/thread open — the list is visible.
  const hoverStore = useHoverStore.getState();
  if (hoverStore.hoveredSenderGroupKey && hoverStore.hoveredSenderGroupEmailIds.length > 0) {
    return {
      kind: 'senderGroup',
      ids: hoverStore.hoveredSenderGroupEmailIds,
      group: hoverStore.hoveredSenderGroupInfo,
    };
  }
  if (hoverStore.hoveredEmailId) {
    return { kind: 'emails', ids: [hoverStore.hoveredEmailId] };
  }

  return { kind: 'none', ids: [] };
}

/**
 * Resolve the SenderGroupInfo for a pointer-hit group row. The hover store's
 * info is used ONLY when its key matches the row under the pointer (a stale
 * hover for a different group must never win — audit F3); otherwise the group
 * is rebuilt STRICTLY from the row's own rendered membership
 * (data-sender-group-email-ids) with its identity validated against the row's
 * data-sender-group-key. Groups are recipient-keyed in Sent, so re-grouping
 * the loaded list by from.email mis-keyed the rebuild to the user's OWN
 * address and dispatched a server-side sender bulk action across the whole
 * loaded list (N1). Returns null when the key can't be matched to an address
 * on the rendered rows — the resolver then degrades to plain row semantics.
 */
function resolvePointerSenderGroup(
  groupKey: string | null,
  ids: string[]
): SenderGroupInfo | null {
  const hoverInfo = useHoverStore.getState().hoveredSenderGroupInfo;
  if (hoverInfo && groupKey && hoverInfo.key === groupKey) {
    return hoverInfo;
  }
  if (!groupKey || !ids.length) {
    return null;
  }
  const emailState = useEmailStore.getState();
  const idSet = new Set(ids);
  const groupEmails = emailState.emails.filter((e) => idSet.has(e.id));
  if (!groupEmails.length) {
    return null;
  }

  // Identify the address the rendered group is keyed on: it must appear as
  // the sender OR a recipient of a rendered member. Refuse the user's own
  // address — a sender bulk action keyed to it amplifies across accounts.
  const matchesKey = (value?: string | null) =>
    Boolean(value && value.trim().toLowerCase() === groupKey);
  if (getOwnAccountEmails().has(groupKey)) {
    return null;
  }
  let identityEmail: string | null = null;
  let identityName: string | null = null;
  for (const email of groupEmails) {
    if (matchesKey(email.from?.email)) {
      identityEmail = email.from.email;
      identityName = email.from.name || email.from.email;
      break;
    }
    for (const list of [email.to, email.cc, email.bcc, email.replyTo]) {
      const hit = list?.find((entry) => matchesKey(entry?.email));
      if (hit?.email) {
        identityEmail = hit.email;
        identityName = hit.name || hit.email;
        break;
      }
    }
    if (identityEmail) break;
  }
  if (!identityEmail) {
    return null;
  }

  return {
    key: groupKey,
    contactId: groupEmails[0].contactId ?? null,
    contactApprovalState: groupEmails[0].contactApprovalState ?? null,
    senderEmail: identityEmail,
    senderName: identityName || identityEmail,
    emails: groupEmails,
    unreadCount: groupEmails.filter((e) => !e.read).length,
    totalMessages: groupEmails.length,
  };
}

/**
 * Run a sender-group quick action with the hover-advance bookkeeping (move the
 * hover anchor past the group that is about to disappear).
 */
function runSenderGroupAction(
  group: SenderGroupInfo | null,
  ids: string[],
  action: SenderGroupQuickAction
): void {
  const emailStore = useEmailStore.getState();
  const hoverStore = useHoverStore.getState();
  let info = group;
  if (!info) {
    const target = emailStore.emails.find((e) => e.id === ids[0]);
    info = target ? buildSenderGroupFromEmail(target, emailStore.emails, new Set()) : null;
  }
  if (!info) {
    // No safe group identity (e.g. own-address rows in Sent) — act on the
    // rendered row ids only instead of silently doing nothing (N1 degrade).
    if (ids.length && (action === 'archive' || action === 'delete' || action === 'markRead'
      || action === 'markUnread' || action === 'approveSender' || action === 'blockSender')) {
      runListAction(action, ids, { advanceHover: true });
    }
    return;
  }

  const groupBySender = useSenderGroupingStore.getState().groupBySender;

  // Find the next email to hover after the action (before emails are removed)
  const emails = emailStore.emails;
  const groupIds = new Set(info.emails.map((e) => e.id));
  const targetId = info.emails[0]?.id;
  const currentIndex = targetId ? emails.findIndex((e) => e.id === targetId) : -1;
  let nextEmail = null;
  if (currentIndex !== -1) {
    for (let i = currentIndex + 1; i < emails.length; i++) {
      if (!groupIds.has(emails[i].id)) {
        nextEmail = emails[i];
        break;
      }
    }
    if (!nextEmail) {
      for (let i = currentIndex - 1; i >= 0; i--) {
        if (!groupIds.has(emails[i].id)) {
          nextEmail = emails[i];
          break;
        }
      }
    }
  }

  void useSenderGroupingStore.getState().handleQuickAction(info, action);

  if (nextEmail) {
    // When sender grouping is active, resolve the full sender group for the next
    // email so that a subsequent press acts on the entire group, not one email.
    if (groupBySender) {
      const nextGroup = buildSenderGroupFromEmail(nextEmail, emails, groupIds);
      if (nextGroup) {
        hoverStore.setHoveredSenderGroup(
          nextGroup.key,
          nextGroup.emails.map((e) => e.id),
          nextGroup,
        );
        return;
      }
    }
    hoverStore.setHoveredEmail(nextEmail.id);
  }
}

/**
 * Apply a bulk-store action to list-row targets, advancing the hover anchor
 * past the rows that are about to disappear.
 */
function runListAction(
  action: 'archive' | 'delete' | 'markRead' | 'markUnread' | 'approveSender' | 'blockSender',
  ids: string[],
  opts: { advanceHover?: boolean } = {}
): void {
  const emailStore = useEmailStore.getState();
  const hoverStore = useHoverStore.getState();
  const emails = emailStore.emails;
  const targetId = ids[0];
  const currentIndex = emails.findIndex((e) => e.id === targetId);
  let nextEmail = null;
  if (currentIndex !== -1 && emails.length > 1) {
    nextEmail = emails[currentIndex + 1] ?? emails[currentIndex - 1] ?? null;
  }

  void useBulkOperationsStore.getState().applyBulkAction(action, ids);

  if (opts.advanceHover && nextEmail) {
    hoverStore.setHoveredEmail(nextEmail.id);
  }
}

/**
 * In the SEARCH view the targeted rows are search results that may not be
 * loaded into the email store — dispatch through the engine with explicit
 * context hints from the search result payloads.
 */
function runSearchEmailAction(
  kind: 'archive' | 'delete' | 'markRead' | 'markUnread',
  ids: string[]
): void {
  const results = useSearchStore.getState().results;
  ids.forEach((id) => {
    let contextAccountId: string | null = null;
    let contextFolderId: string | null = null;
    for (const result of results) {
      if (result.kind === 'email' && result.email.id === id) {
        contextAccountId = result.accountId ?? result.email.accountId ?? null;
        contextFolderId = result.email.folderId ?? null;
        break;
      }
      if (result.kind === 'thread') {
        const message = result.thread.messages.find((m) => m.id === id);
        if (message) {
          contextAccountId = result.accountId ?? message.accountId ?? null;
          contextFolderId = message.folderId ?? null;
          break;
        }
      }
    }
    void executeEmailAction(kind, { kind: 'messages', ids: [id] }, {
      contextAccountId,
      contextFolderId,
    }).then((result) => {
      if (result.failedIds.includes(id)) {
        return;
      }
      // Refresh the search rows so the result list reflects the action
      // (F15 refresh half): flip read state, or drop removed rows.
      useSearchStore.setState((state) => {
        if (kind === 'markRead' || kind === 'markUnread') {
          const read = kind === 'markRead';
          let changed = false;
          const nextResults = state.results.map((entry) => {
            if (entry.kind === 'email' && entry.email.id === id && entry.email.read !== read) {
              changed = true;
              return { ...entry, email: { ...entry.email, read } };
            }
            if (entry.kind === 'thread') {
              const messages = entry.thread.messages.map((message) =>
                message.id === id && message.read !== read ? { ...message, read } : message
              );
              if (messages.some((message, index) => message !== entry.thread.messages[index])) {
                changed = true;
                return {
                  ...entry,
                  thread: {
                    ...entry.thread,
                    messages,
                    unreadCount: messages.filter((message) => !message.read).length,
                  },
                };
              }
            }
            return entry;
          });
          return changed ? { results: nextResults } : {};
        }
        // archive/delete: drop the row (thread rows lose the member)
        let changed = false;
        const nextResults = state.results.flatMap((entry) => {
          if (entry.kind === 'email' && entry.email.id === id) {
            changed = true;
            return [];
          }
          if (entry.kind === 'thread') {
            const messages = entry.thread.messages.filter((message) => message.id !== id);
            if (messages.length !== entry.thread.messages.length) {
              changed = true;
              if (!messages.length) {
                return [];
              }
              return [{
                ...entry,
                thread: {
                  ...entry.thread,
                  messages,
                  messageCount: messages.length,
                  unreadCount: messages.filter((message) => !message.read).length,
                },
              }];
            }
          }
          return [entry];
        });
        return changed ? { results: nextResults } : {};
      });
    });
  });
}

/**
 * Conversation-level shortcut pressed while the open thread is still a
 * provisional shell: refuse loudly rather than act on the seed message alone
 * (mirrors the thread toolbar, which disables these until hydrated).
 */
function toastThreadStillLoading(): void {
  toast.info('Conversation is still loading — try again in a moment');
}

/** Whether any actionable target exists (used by enabled() gates). */
function hasActionTarget(): boolean {
  const target = resolveActionTarget();
  if (target.kind === 'none') return false;
  if (target.kind === 'triage') return true;
  return target.ids.length > 0;
}

function resolveEmailFromState(
  emailState: ReturnType<typeof useEmailStore.getState>,
  emailId: string
) {
  if (emailState.selectedEmail?.id === emailId) {
    return emailState.selectedEmail;
  }
  const listEmail = emailState.emails.find((email) => email.id === emailId);
  if (listEmail) {
    return listEmail;
  }
  const threadMessage = emailState.selectedThread?.messages?.find((message) => message.id === emailId);
  return threadMessage ?? null;
}

/**
 * Get the email to reply to:
 * - If a thread is selected, use the last non-archived message
 * - Otherwise use the selected email
 */
function getReplyTargetEmail() {
  const emailState = useEmailStore.getState();

  if (emailState.selectedThread?.messages?.length) {
    const messages = emailState.selectedThread.messages;
    const active = messages.filter((m) => !m.isArchived && !m.isTrashed);
    return active[active.length - 1] ?? messages[messages.length - 1] ?? null;
  }

  return emailState.selectedEmail ?? null;
}

function hasTargetContacts(): boolean {
  const target = resolveActionTarget();
  if (target.kind === 'none') return false;
  if (target.kind === 'triage') {
    return useTriageStore.getState().currentContext?.contactId != null;
  }
  // Sender-level actions resolve the contact server-side by email per account
  if (target.kind === 'senderGroup') return target.ids.length > 0;
  const emailState = useEmailStore.getState();
  return target.ids.some((id) => Boolean(resolveEmailFromState(emailState, id)?.contactId));
}

// Register all default commands
export function registerDefaultCommands() {
  // ═══════════════════════════════════════════════════
  // COMPOSE ACTIONS
  // ═══════════════════════════════════════════════════

  keyboardRegistry.register({
    action: 'compose:new',
    label: 'New Email',
    description: 'Open compose dialog for new email',
    category: 'compose',
    defaultBinding: { key: 'c', ctrl: true },
    handler: () => {
      useUIStore.getState().setComposeOpen(true);
    },
  });

  keyboardRegistry.register({
    action: 'compose:discard',
    label: 'Close Compose',
    description: 'Close compose dialog without sending',
    category: 'compose',
    defaultBinding: { key: 'Escape' },
    handler: () => {
      const uiStore = useUIStore.getState();
      if (uiStore.composeOpen) {
        uiStore.setComposeOpen(false);
      }
    },
    enabled: () => useUIStore.getState().composeOpen,
    // Escape contention: the compose dialog is a modal overlay, so it must win
    // over selection:clear / triage:close when several are enabled at once
    // (audit §2.17 #4). See ESCAPE_PRIORITY note on selection:clear/triage:close.
    priority: 100,
  });

  // ═══════════════════════════════════════════════════
  // EMAIL ACTIONS
  // ═══════════════════════════════════════════════════

  keyboardRegistry.register({
    action: 'email:archive',
    label: 'Archive',
    description: 'Archive the open conversation, the current sender group/digest, or the hovered/selected email(s)',
    category: 'email',
    defaultBinding: { key: 'e' },
    handler: async () => {
      const target = resolveActionTarget();
      switch (target.kind) {
        case 'none':
          return;
        case 'triage':
          useTriageStore.getState().archiveSender();
          return;
        case 'thread': {
          if (!target.complete) {
            toastThreadStillLoading();
            return;
          }
          if (target.ids.length) {
            // Explicit message enumeration → engine 'messages' target
            void executeEmailAction('archive', { kind: 'messages', ids: target.ids });
            // Navigate back to the list since the thread is empty now
            useEmailStore.getState().clearSelection();
          }
          return;
        }
        case 'senderGroup':
          runSenderGroupAction(target.group, target.ids, 'archive');
          return;
        case 'emails':
          if (useUIStore.getState().view === 'search') {
            runSearchEmailAction('archive', target.ids);
            return;
          }
          runListAction('archive', target.ids, { advanceHover: true });
          return;
      }
    },
    enabled: hasActionTarget,
  });

  keyboardRegistry.register({
    action: 'email:delete',
    label: 'Delete',
    description: 'Delete the open conversation, the current sender group/digest, or the hovered/selected email(s)',
    category: 'email',
    defaultBinding: { key: 'Delete' },
    handler: async () => {
      const target = resolveActionTarget();
      switch (target.kind) {
        case 'none':
          return;
        case 'triage':
          useTriageStore.getState().deleteSender();
          return;
        case 'thread': {
          if (!target.complete) {
            toastThreadStillLoading();
            return;
          }
          if (target.ids.length) {
            void executeEmailAction('delete', { kind: 'messages', ids: target.ids });
            useEmailStore.getState().clearSelection();
          }
          return;
        }
        case 'senderGroup':
          runSenderGroupAction(target.group, target.ids, 'delete');
          return;
        case 'emails':
          if (useUIStore.getState().view === 'search') {
            runSearchEmailAction('delete', target.ids);
            return;
          }
          runListAction('delete', target.ids, { advanceHover: true });
          return;
      }
    },
    enabled: hasActionTarget,
  });

  keyboardRegistry.register({
    action: 'email:moveToFolder',
    label: 'Move to Folder',
    description: 'Open folder picker to move hovered or selected email(s)',
    category: 'email',
    defaultBinding: { key: 'v' },
    handler: () => {
      const target = resolveActionTarget();
      if (target.kind === 'none' || target.kind === 'triage') return;
      if (target.kind === 'thread' && !target.complete) {
        toastThreadStillLoading();
        return;
      }
      if (target.ids.length) {
        // Search rows may not be in the email store — hand the picker their
        // account/folder from the search payloads (N16) so the move doesn't
        // fail context resolution.
        const contextHints: Record<string, { accountId: string; folderId: string | null }> = {};
        if (useUIStore.getState().view === 'search') {
          const results = useSearchStore.getState().results;
          target.ids.forEach((id) => {
            for (const result of results) {
              if (result.kind === 'email' && result.email.id === id) {
                const accountId = result.accountId ?? result.email.accountId ?? null;
                if (accountId) {
                  contextHints[id] = { accountId, folderId: result.email.folderId ?? null };
                }
                break;
              }
              if (result.kind === 'thread') {
                const message = result.thread.messages.find((m) => m.id === id);
                if (message) {
                  const accountId = result.accountId ?? message.accountId ?? null;
                  if (accountId) {
                    contextHints[id] = { accountId, folderId: message.folderId ?? null };
                  }
                  break;
                }
              }
            }
          });
        }
        useMoveToFolderStore.getState().open(target.ids, contextHints);
      }
    },
    enabled: () => {
      const target = resolveActionTarget();
      return target.kind !== 'none' && target.kind !== 'triage' && target.ids.length > 0;
    },
  });

  keyboardRegistry.register({
    action: 'email:markRead',
    label: 'Toggle Read/Unread',
    description: 'Toggle read status of hovered or selected email(s)',
    category: 'email',
    defaultBinding: { key: 'u' },
    handler: () => {
      const target = resolveActionTarget();
      switch (target.kind) {
        case 'none':
        case 'triage':
          return;
        case 'senderGroup': {
          const action = (target.group?.unreadCount ?? 1) > 0 ? 'markRead' : 'markUnread';
          runSenderGroupAction(target.group, target.ids, action);
          return;
        }
        case 'thread': {
          if (!target.complete) {
            toastThreadStillLoading();
            return;
          }
          const emailState = useEmailStore.getState();
          const firstEmail = resolveEmailFromState(emailState, target.ids[0]);
          void executeEmailAction(firstEmail?.read ? 'markUnread' : 'markRead', {
            kind: 'messages',
            ids: target.ids,
          });
          return;
        }
        case 'emails': {
          const emailState = useEmailStore.getState();
          const firstEmail = resolveEmailFromState(emailState, target.ids[0]);
          if (useUIStore.getState().view === 'search') {
            // Search rows may not be loaded in the email store — resolve the
            // toggle direction from the SEARCH RESULTS, or a store-absent read
            // row could never be marked unread (N16).
            let searchRead: boolean | undefined;
            for (const result of useSearchStore.getState().results) {
              if (result.kind === 'email' && result.email.id === target.ids[0]) {
                searchRead = result.email.read;
                break;
              }
              if (result.kind === 'thread') {
                const message = result.thread.messages.find((m) => m.id === target.ids[0]);
                if (message) {
                  searchRead = message.read;
                  break;
                }
              }
            }
            const searchAction = (searchRead ?? firstEmail?.read) ? 'markUnread' : 'markRead';
            runSearchEmailAction(searchAction, target.ids);
            return;
          }
          const action = firstEmail?.read ? 'markUnread' : 'markRead';
          runListAction(action, target.ids);
          return;
        }
      }
    },
    enabled: hasActionTarget,
  });

  keyboardRegistry.register({
    action: 'email:reply',
    label: 'Reply',
    description: 'Reply to the selected email',
    category: 'email',
    defaultBinding: { key: 'r' },
    handler: () => {
      const email = getReplyTargetEmail();
      if (email) useUIStore.getState().setPendingReply({ mode: 'reply', emailId: email.id });
    },
    enabled: () => getReplyTargetEmail() !== null,
  });

  keyboardRegistry.register({
    action: 'email:replyAll',
    label: 'Reply All',
    description: 'Reply all to the selected email',
    category: 'email',
    defaultBinding: { key: 'r', shift: true },
    handler: () => {
      const email = getReplyTargetEmail();
      if (email) useUIStore.getState().setPendingReply({ mode: 'replyAll', emailId: email.id });
    },
    enabled: () => getReplyTargetEmail() !== null,
  });

  keyboardRegistry.register({
    action: 'email:forward',
    label: 'Forward',
    description: 'Forward the selected email',
    category: 'email',
    defaultBinding: { key: 'f' },
    handler: () => {
      const email = getReplyTargetEmail();
      if (email) useUIStore.getState().setPendingReply({ mode: 'forward', emailId: email.id });
    },
    enabled: () => getReplyTargetEmail() !== null,
  });

  keyboardRegistry.register({
    action: 'email:approveSender',
    label: 'Approve Sender/Contact',
    description: 'Approve hovered or selected senders in lists, detail view, Contacts & Moderation, or triage mode',
    category: 'email',
    defaultBinding: { key: 'a' },
    handler: async () => {
      const triageStore = useTriageStore.getState();

      // Triage mode: approve current sender (optimistic update). In unified
      // mode the sender can exist as a separate contact in EVERY account —
      // fan out across accountIds via the sender bulk action (resolved by
      // email per account), exactly like triage archive/delete already do.
      if (triageStore.isTriageMode && triageStore.currentContext) {
        const { accountIds, folderId, senderEmail, contactId } = triageStore.currentContext;
        if (!contactId) {
          toast.error('No contact to approve');
          return;
        }
        // Mark processed + drop the card (E9) so an approve/block-only session
        // still triggers the close reconciliation reload (setTriageMode keys it
        // on processedSenders) and advances like archive/delete.
        const senderKey = senderEmail.trim().toLowerCase();
        triageStore.markSenderProcessed(senderKey);
        triageStore.removeSender(senderKey);
        // Run approve in background
        (async () => {
          try {
            const { performSenderBulkAction } = await import('@/api/senders');
            const results = await Promise.allSettled(
              accountIds.map((acctId) =>
                performSenderBulkAction({
                  accountId: acctId,
                  folderId,
                  sender: { email: senderEmail },
                  action: 'approve',
                })
              )
            );
            const failed = results.filter((r) => r.status === 'rejected').length;
            if (failed === results.length) {
              toast.error('Failed to approve sender');
            } else if (failed > 0) {
              toast.error(`Approved sender, but ${failed} account(s) failed`);
            } else {
              toast.success('Approved sender');
            }
          } catch (error) {
            console.error('Failed to approve sender:', error);
            toast.error('Failed to approve sender');
          }
        })();
        return;
      }

      const target = resolveActionTarget();
      if (target.kind === 'senderGroup') {
        runSenderGroupAction(target.group, target.ids, 'approveSender');
        return;
      }
      // Open thread (N21): enabled() reported this target available but the
      // handler had no case — a silent no-op while reading a conversation.
      if (target.kind === 'thread' && target.ids.length) {
        runListAction('approveSender', target.ids);
        return;
      }
      if (target.kind === 'emails' && target.ids.length) {
        runListAction('approveSender', target.ids, { advanceHover: true });
      }
    },
    enabled: hasTargetContacts,
  });

  keyboardRegistry.register({
    action: 'email:blockSender',
    label: 'Block Sender/Contact',
    description: 'Block hovered or selected senders in lists, detail view, Contacts & Moderation, or triage mode',
    category: 'email',
    defaultBinding: { key: 'b' },
    handler: async () => {
      const triageStore = useTriageStore.getState();

      // Triage mode: block current sender (optimistic update). Fans out across
      // every account the sender appears in (see approve above).
      if (triageStore.isTriageMode && triageStore.currentContext) {
        const { accountIds, folderId, senderEmail, contactId } = triageStore.currentContext;
        if (!contactId) {
          toast.error('No contact to block');
          return;
        }
        // Mark processed + drop the card (E9) so an approve/block-only session
        // still triggers the close reconciliation reload (setTriageMode keys it
        // on processedSenders) and advances like archive/delete.
        const senderKey = senderEmail.trim().toLowerCase();
        triageStore.markSenderProcessed(senderKey);
        triageStore.removeSender(senderKey);
        // Run block in background
        (async () => {
          try {
            const { performSenderBulkAction } = await import('@/api/senders');
            const results = await Promise.allSettled(
              accountIds.map((acctId) =>
                performSenderBulkAction({
                  accountId: acctId,
                  folderId,
                  sender: { email: senderEmail },
                  action: 'block',
                })
              )
            );
            const failed = results.filter((r) => r.status === 'rejected').length;
            if (failed === results.length) {
              toast.error('Failed to block sender');
            } else if (failed > 0) {
              toast.error(`Blocked sender, but ${failed} account(s) failed`);
            } else {
              toast.success('Blocked sender');
            }
          } catch (error) {
            console.error('Failed to block sender:', error);
            toast.error('Failed to block sender');
          }
        })();
        return;
      }

      const target = resolveActionTarget();
      if (target.kind === 'senderGroup') {
        runSenderGroupAction(target.group, target.ids, 'blockSender');
        return;
      }
      // Open thread (N21): see approve above.
      if (target.kind === 'thread' && target.ids.length) {
        runListAction('blockSender', target.ids);
        return;
      }
      if (target.kind === 'emails' && target.ids.length) {
        runListAction('blockSender', target.ids, { advanceHover: true });
      }
    },
    enabled: hasTargetContacts,
  });

  // ═══════════════════════════════════════════════════
  // SELECTION ACTIONS
  // ═══════════════════════════════════════════════════

  keyboardRegistry.register({
    action: 'selection:clear',
    label: 'Clear Selection',
    description: 'Clear bulk selection and exit selection mode',
    category: 'selection',
    defaultBinding: { key: 'Escape' },
    handler: () => {
      const bulkStore = useBulkOperationsStore.getState();
      if (bulkStore.bulkSelection.size > 0) {
        bulkStore.clearSelection();
        bulkStore.setBulkEditMode(false);
      }
    },
    enabled: () => useBulkOperationsStore.getState().bulkSelection.size > 0,
    // ESCAPE_PRIORITY: the generic clear is the lowest of the contended Escape
    // handlers. compose:discard (100) and triage:close (50) are context-specific
    // and intentionally win when both they and a selection are active, so Escape
    // dismisses the active context first instead of resolving by registration
    // order (audit §2.17 #4).
    priority: 10,
  });

  keyboardRegistry.register({
    action: 'selection:selectAll',
    label: 'Select All',
    description: 'Select all visible emails in the current mail view',
    category: 'selection',
    defaultBinding: { key: 'a', ctrl: true },
    handler: () => {
      // Answered by the mounted EmailList with ITS visible (filtered/grouped)
      // ids — never the raw loaded store list, which can include rows hidden by
      // the unread/label filters.
      mailEvents.emit('mail:selectAllVisible', undefined);
    },
    enabled: () => {
      const uiState = useUIStore.getState();
      // Gated on no open detail/thread (N3): with a detail open the list is
      // hidden but still mounted — it would answer with an INVISIBLE bulk
      // selection that outranks the open email for the next e/Delete.
      const emailState = useEmailStore.getState();
      return (
        uiState.activeWorkspace === 'mail' &&
        uiState.view === 'mailbox' &&
        !uiState.composeOpen &&
        !emailState.selectedEmailId &&
        !emailState.selectedThread
      );
    },
  });

  keyboardRegistry.register({
    action: 'email:exitSenderGroup',
    label: 'Exit Sender Group',
    description: 'Leave the open sender group and return to the mailbox list',
    category: 'email',
    defaultBinding: { key: 'Escape' },
    handler: () => {
      const senderStore = useSenderGroupingStore.getState();
      const group = senderStore.activeSenderGroup;
      if (!group) return;
      const actionInProgress = Boolean(senderStore.pendingGroupActions[group.key]);
      void senderStore.exitSenderGroup(actionInProgress ? { skipReload: true } : undefined);
    },
    enabled: () =>
      Boolean(useSenderGroupingStore.getState().activeSenderGroup) &&
      useBulkOperationsStore.getState().bulkSelection.size === 0 &&
      // Escape is whitelisted through the dispatcher's input guard, so this
      // command needs its own typing check (F8): Escape inside an inline
      // reply — which doesn't set composeOpen — must not tear down the group.
      !isTypingContext(),
    // ESCAPE_PRIORITY: between selection:clear (10) and triage:close (50) —
    // an active selection clears first; triage/compose contexts still win.
    // Replaces the old un-prioritized window listener in useEmailListKeyboard,
    // which double-fired alongside the registry and ran inside text inputs.
    priority: 30,
  });

  // ═══════════════════════════════════════════════════
  // NAVIGATION ACTIONS
  // ═══════════════════════════════════════════════════

  keyboardRegistry.register({
    action: 'navigation:nextEmail',
    label: 'Next Email',
    description: 'Navigate to next email in list',
    category: 'navigation',
    defaultBinding: { key: 'j' },
    handler: () => {
      const emailStore = useEmailStore.getState();
      const { emails, selectedEmailId } = emailStore;

      if (emails.length === 0) return;

      // If no email selected, select the first one
      if (!selectedEmailId) {
        const firstEmail = emails[0];
        emailStore.selectEmail(
          firstEmail.id,
          firstEmail.accountId,
          firstEmail.folderId ?? '',
          { threadId: firstEmail.threadId ?? null }
        );
        return;
      }

      // Find current email index
      const currentIndex = emails.findIndex((email) => email.id === selectedEmailId);
      if (currentIndex === -1 || currentIndex >= emails.length - 1) return;

      // Select next email
      const nextEmail = emails[currentIndex + 1];
      emailStore.selectEmail(
        nextEmail.id,
        nextEmail.accountId,
        nextEmail.folderId ?? '',
        { threadId: nextEmail.threadId ?? null }
      );
    },
    // View-gated (F15): in the search view the store list is the HIDDEN
    // mailbox — j must not open its first email behind the results.
    enabled: () =>
      useUIStore.getState().view !== 'search' &&
      useEmailStore.getState().emails.length > 0,
  });

  keyboardRegistry.register({
    action: 'navigation:prevEmail',
    label: 'Previous Email',
    description: 'Navigate to previous email in list',
    category: 'navigation',
    defaultBinding: { key: 'k' },
    handler: () => {
      const emailStore = useEmailStore.getState();
      const { emails, selectedEmailId } = emailStore;

      if (emails.length === 0) return;

      // If no email selected, select the first one
      if (!selectedEmailId) {
        const firstEmail = emails[0];
        emailStore.selectEmail(
          firstEmail.id,
          firstEmail.accountId,
          firstEmail.folderId ?? '',
          { threadId: firstEmail.threadId ?? null }
        );
        return;
      }

      // Find current email index
      const currentIndex = emails.findIndex((email) => email.id === selectedEmailId);
      if (currentIndex <= 0) return;

      // Select previous email
      const prevEmail = emails[currentIndex - 1];
      emailStore.selectEmail(
        prevEmail.id,
        prevEmail.accountId,
        prevEmail.folderId ?? '',
        { threadId: prevEmail.threadId ?? null }
      );
    },
    // View-gated (F15): see navigation:nextEmail.
    enabled: () =>
      useUIStore.getState().view !== 'search' &&
      useEmailStore.getState().emails.length > 0,
  });

  // ═══════════════════════════════════════════════════
  // SEARCH ACTIONS
  // ═══════════════════════════════════════════════════

  keyboardRegistry.register({
    action: 'search:commandPalette',
    label: 'Command Palette',
    description: 'Open command palette to search and run commands',
    category: 'search',
    defaultBinding: { key: 'k', ctrl: true },
    handler: () => {
      useCommandPaletteStore.getState().toggle();
    },
  });

  // ═══════════════════════════════════════════════════
  // SETTINGS ACTIONS
  // ═══════════════════════════════════════════════════

  keyboardRegistry.register({
    action: 'settings:open',
    label: 'Open Settings',
    description: 'Open settings dialog',
    category: 'settings',
    defaultBinding: { key: ',', ctrl: true },
    handler: () => {
      useUIStore.getState().setSettingsDialogOpen(true);
    },
  });

  keyboardRegistry.register({
    action: 'todo:newTask',
    label: 'New Task',
    description: 'Open quick add for tasks',
    category: 'todo',
    defaultBinding: { key: 't', ctrl: true },
    handler: () => {
      useUIStore.getState().setActiveWorkspace('todo');
      useUIStore.getState().triggerPrimaryAction();
    },
  });

  keyboardRegistry.register({
    action: 'todo:viewToday',
    label: 'Show Today view',
    description: 'Switch Todo workspace to Today view',
    category: 'todo',
    defaultBinding: { key: '1', ctrl: true },
    handler: () => {
      useUIStore.getState().setActiveWorkspace('todo');
      useTodoViewStore.getState().setActiveView('today');
    },
  });

  keyboardRegistry.register({
    action: 'todo:viewUpcoming',
    label: 'Show Upcoming view',
    description: 'Switch Todo workspace to Upcoming view',
    category: 'todo',
    defaultBinding: { key: '2', ctrl: true },
    handler: () => {
      useUIStore.getState().setActiveWorkspace('todo');
      useTodoViewStore.getState().setActiveView('upcoming');
    },
  });

  keyboardRegistry.register({
    action: 'todo:viewAnytime',
    label: 'Show Anytime view',
    description: 'Switch Todo workspace to Anytime view',
    category: 'todo',
    defaultBinding: { key: '3', ctrl: true },
    handler: () => {
      useUIStore.getState().setActiveWorkspace('todo');
      useTodoViewStore.getState().setActiveView('anytime');
    },
  });

  keyboardRegistry.register({
    action: 'todo:viewSomeday',
    label: 'Show Someday view',
    description: 'Switch Todo workspace to Someday view',
    category: 'todo',
    defaultBinding: { key: '4', ctrl: true },
    handler: () => {
      useUIStore.getState().setActiveWorkspace('todo');
      useTodoViewStore.getState().setActiveView('someday');
    },
  });

  keyboardRegistry.register({
    action: 'todo:selectAll',
    label: 'Select All Tasks',
    description: 'Select all tasks in the current view',
    category: 'todo',
    defaultBinding: { key: 'a', ctrl: true, shift: true },
    handler: () => {
      emitTodoCommand({ type: 'select_all' });
    },
  });

  keyboardRegistry.register({
    action: 'todo:completeSelection',
    label: 'Complete selected tasks',
    description: 'Mark selected tasks as complete',
    category: 'todo',
    defaultBinding: { key: '.', ctrl: true },
    handler: () => emitTodoCommand({ type: 'complete_selection' }),
  });

  keyboardRegistry.register({
    action: 'todo:cancelSelection',
    label: 'Cancel selected tasks',
    description: 'Mark selected tasks as cancelled',
    category: 'todo',
    defaultBinding: { key: '.', ctrl: true, shift: true },
    handler: () => emitTodoCommand({ type: 'cancel_selection' }),
  });

  keyboardRegistry.register({
    action: 'todo:deleteSelection',
    label: 'Delete selected tasks',
    description: 'Move selected tasks to trash',
    category: 'todo',
    defaultBinding: { key: 'Backspace', shift: true },
    handler: () => emitTodoCommand({ type: 'delete_selection' }),
  });

  keyboardRegistry.register({
    action: 'todo:somedayOn',
    label: 'Move selection to Someday',
    description: 'Flag selected tasks as Someday',
    category: 'todo',
    defaultBinding: { key: 's', shift: true },
    handler: () => emitTodoCommand({ type: 'someday_on' }),
  });

  keyboardRegistry.register({
    action: 'todo:somedayOff',
    label: 'Make selection active',
    description: 'Remove Someday flag from selected tasks',
    category: 'todo',
    defaultBinding: { key: 'a', shift: true },
    handler: () => emitTodoCommand({ type: 'someday_off' }),
  });

  keyboardRegistry.register({
    action: 'todo:postponeSelection',
    label: 'Postpone selected tasks',
    description: 'Move selected tasks to tomorrow',
    category: 'todo',
    defaultBinding: { key: 'ArrowRight', shift: true },
    handler: () => emitTodoCommand({ type: 'postpone_selection' }),
  });

  // ═══════════════════════════════════════════════════
  // GLOBAL ACTIONS
  // ═══════════════════════════════════════════════════

  keyboardRegistry.register({
    action: 'global:undo',
    label: 'Undo',
    description: 'Undo last email action',
    category: 'global',
    defaultBinding: { key: 'z', ctrl: true },
    handler: async () => {
      const success = await useUndoStore.getState().undo();
      if (success) {
        toast.success('Action undone');
      } else {
        toast.error('Failed to undo');
      }
    },
    enabled: () => useUndoStore.getState().canUndo(),
  });

  keyboardRegistry.register({
    action: 'global:redo',
    label: 'Redo',
    description: 'Redo last undone action',
    category: 'global',
    defaultBinding: { key: 'z', ctrl: true, shift: true },
    handler: async () => {
      const success = await useUndoStore.getState().redo();
      if (success) {
        toast.success('Action redone');
      } else {
        toast.error('Failed to redo');
      }
    },
    enabled: () => useUndoStore.getState().canRedo(),
  });

  // ═══════════════════════════════════════════════════
  // TRIAGE ACTIONS
  // ═══════════════════════════════════════════════════

  keyboardRegistry.register({
    action: 'triage:open',
    label: 'Open Triage Mode',
    description: 'Enter inbox triage mode for quick email processing',
    category: 'triage',
    defaultBinding: { key: 'i', shift: true },
    handler: () => {
      // Triage is a mail sub-view tab now; TriagePage's mount lifecycle sets
      // the store's isTriageMode flag.
      navigateToMailView('triage');
    },
    enabled: () => !useTriageStore.getState().isTriageMode,
  });

  keyboardRegistry.register({
    action: 'triage:close',
    label: 'Close Triage Mode',
    description: 'Exit triage mode and return to normal view',
    category: 'triage',
    defaultBinding: { key: 'Escape' },
    handler: () => {
      navigateToMailView('mailbox');
      useHoverStore.getState().setHoveredSenderGroup(null, []);
    },
    enabled: () => useTriageStore.getState().isTriageMode,
    // ESCAPE_PRIORITY: triage mode is a context, so Escape exits it ahead of the
    // generic selection:clear (10) but below the compose modal (100). Without an
    // explicit priority this could never win against selection:clear, which was
    // registered first (audit §2.17 #4).
    priority: 50,
  });

  keyboardRegistry.register({
    action: 'triage:skip',
    label: 'Skip Sender',
    description: 'Skip current sender and move to next',
    category: 'triage',
    defaultBinding: { key: 's' },
    handler: () => {
      const triageStore = useTriageStore.getState();
      triageStore.nextSender();
    },
    enabled: () => useTriageStore.getState().isTriageMode,
  });

  keyboardRegistry.register({
    action: 'triage:next',
    label: 'Next Sender',
    description: 'Navigate to next sender in triage',
    category: 'triage',
    defaultBinding: { key: 'ArrowRight' },
    handler: () => {
      useTriageStore.getState().nextSender();
    },
    enabled: () => useTriageStore.getState().isTriageMode,
  });

  keyboardRegistry.register({
    action: 'triage:previous',
    label: 'Previous Sender',
    description: 'Navigate to previous sender in triage',
    category: 'triage',
    defaultBinding: { key: 'ArrowLeft' },
    handler: () => {
      const triageStore = useTriageStore.getState();
      if (triageStore.currentIndex > 0) {
        triageStore.prevSender();
      }
    },
    enabled: () => {
      const triageStore = useTriageStore.getState();
      return triageStore.isTriageMode && triageStore.currentIndex > 0;
    },
  });
}
